// ── Old-platform (ACE 1.0) accounts ─────────────────────────────────────────
//
// The old Plaid connections still feed ACE 1.0, and its transactions are
// copied into dbo.TransactionMIGRATION from time to time. Each old bank
// account needs two answers here — which BORROWER and which ACE BANK ACCOUNT —
// before its transactions mean anything. This panel records both and brings
// the transactions across.
//
// Unlike the queue above, it lists an old account whether or not any of its
// transactions have landed yet. An account opened after the first migration
// (a borrower moving banks) has nothing here until it is mapped, so the queue
// could never show it.
import { useState, useEffect, useCallback } from "react";
import { B } from "../../../shared/theme";
import { fmtDate } from "../../../shared/format";
import { Btn, Card, Field, Modal, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

// ── Setting something aside ──
// Shared with the queue on the same page: an old account or an unmapped stream
// that does not need mapping (the lender's own account, a test connection, a
// closed account nobody needs) leaves the to-do list but is never deleted.
export const IGNORE_REASONS = {
  aequum: "Our own account",
  notborrower: "Not a borrower account",
  closed: "Closed — not needed",
  test: "Test or duplicate connection",
  other: "Other",
};
export const IgnoreModal = ({ title, kind, subjectkey, onClose, onDone }) => {
  const { api } = useApp();
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setErr(null);
    try { await api.post("saveBankMappingIgnore", { kind, key: String(subjectkey), reason, note }); onDone(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <Modal title={title} onClose={onClose}>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 12 }}>
        It stops showing as needing a mapping. Nothing is deleted — find it again under <b>Show ignored</b> and restore it.
      </div>
      <Field label="Why" required>
        <select value={reason} onChange={e => setReason(e.target.value)}
                style={{ width: "100%", padding: "7px 10px", fontSize: 13, borderRadius: 4, border: "1px solid #999" }}>
          <option value="">— choose —</option>
          {Object.entries(IGNORE_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </Field>
      <Field label="Note">
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                  style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 13, borderRadius: 4, border: "1px solid #999", fontFamily: "inherit" }} />
      </Field>
      {err && <div style={{ color: "#993C1D", fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={!reason || busy} onClick={save}>{busy ? "Saving…" : "Ignore"}</Btn>
      </div>
    </Modal>
  );
};

const STATUS = {
  "Ignored":              { bg: "#F1EFE8", fg: "#5F5E5A" },
  "Needs borrower":       { bg: "#FAECE7", fg: "#993C1D" },
  "Needs bank account":   { bg: "#FDF0D5", fg: "#7A5200" },
  "Transactions waiting": { bg: "#E6F1FB", fg: "#185FA5" },
  "Up to date":           { bg: "#E1F5EE", fg: "#0F6E56" },
};
const num = n => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const sel = { fontSize: 12, padding: "4px 6px", borderRadius: 4, border: "1px solid #b8bfc6", maxWidth: 240, background: "#fff" };

const Row = ({ r, accounts, bankAccounts, onSaved, canEdit }) => {
  const { api, setConfirm } = useApp();
  const [acct, setAcct] = useState(r.accountid || "");
  const [ba, setBa] = useState(r.acebankaccountid || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ignoring, setIgnoring] = useState(false);
  const ignored = r.status === "Ignored";
  const restore = async () => {
    setBusy(true); setErr(null);
    try { await api.post("saveBankMappingIgnore", { kind: "old", key: String(r.BankAccountId), restore: true }); onSaved(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const choices = bankAccounts.filter(b => String(b.accountid) === String(acct));
  const dirty = (acct || "") !== (r.accountid || "") || String(ba || "") !== String(r.acebankaccountid || "");

  const save = async (force = false) => {
    setBusy(true); setErr(null);
    try {
      await api.post("saveBankMigrationMap", { old: r.BankAccountId, accountid: acct || null, bankaccountid: ba || null, force });
      onSaved();
    } catch (e) {
      if (/different borrower\. Confirm/.test(e.message || "")) {
        setConfirm({
          title: "Move to another borrower?",
          confirmLabel: "Move",
          msg: `Old account ${r.BankAccountId} is mapped to ${r.companyname}. Moving it also moves its ${num(r.landed_txns)} transactions already in ACE.`,
          onConfirm: () => save(true),
        });
      } else setErr(e.message);
    }
    setBusy(false);
  };

  const st = STATUS[r.status] || STATUS["Up to date"];
  return (
    <tr>
      <Td>
        <div style={{ fontWeight: 600 }}>#{r.BankAccountId}{r.sourcename ? ` · ${r.sourcename}` : ""}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
          {[r.sourcebank, r.sourcemask ? "••" + r.sourcemask : null].filter(Boolean).join(" ") || "no name or mask on file"}
        </div>
      </Td>
      <Td>
        <div style={{ fontVariantNumeric: "tabular-nums" }}>{num(r.src_posted)}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{fmtDate(r.src_first)} – {fmtDate(r.src_last)}</div>
        {r.sample && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={r.sample}>{r.sample}</div>}
      </Td>
      <Td style={{ fontVariantNumeric: "tabular-nums" }}>{num(r.landed_txns)}
        {r.landed_last && <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>to {fmtDate(r.landed_last)}</div>}</Td>
      <Td>
        {canEdit
          ? <select value={acct} onChange={e => { setAcct(e.target.value); setBa(""); }} style={sel}>
              <option value="">— borrower —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.companyname}</option>)}
            </select>
          : (r.companyname || "—")}
      </Td>
      <Td>
        {canEdit
          ? <select value={ba} onChange={e => setBa(e.target.value)} style={sel} disabled={!acct}>
              <option value="">{acct ? "— bank account —" : "choose borrower first"}</option>
              {choices.map(b => <option key={b.id} value={b.id}>
                {b.bankname || "Account"} ••{b.last4}{b.closed ? " (closed)" : ""}</option>)}
            </select>
          : (r.ace_bankname ? `${r.ace_bankname} ••${r.ace_last4}` : "—")}
      </Td>
      <Td><span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: st.bg, color: st.fg, whiteSpace: "nowrap" }}>{r.status}</span>
        {ignored && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 3, maxWidth: 220 }}
                         title={r.ignorenote || ""}>{IGNORE_REASONS[r.ignorereason] || r.ignorereason}{r.ignoredby ? ` · ${r.ignoredby}` : ""}</div>}
        {err && <div style={{ fontSize: 11, color: "#993C1D", marginTop: 3, maxWidth: 220 }}>{err}</div>}</Td>
      <Td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        {canEdit && !ignored && <Btn variant={dirty ? "primary" : "default"} disabled={!dirty || busy || !acct} onClick={() => save(false)}>
          {busy ? "…" : "Save"}</Btn>}
        {canEdit && !ignored && r.status !== "Up to date" && <span style={{ marginLeft: 6 }}><Btn onClick={() => setIgnoring(true)}>Ignore</Btn></span>}
        {canEdit && ignored && <Btn disabled={busy} onClick={restore}>{busy ? "…" : "Restore"}</Btn>}
        {ignoring && <IgnoreModal title={`Ignore old account #${r.BankAccountId}`} kind="old" subjectkey={r.BankAccountId}
                                  onClose={() => setIgnoring(false)} onDone={() => { setIgnoring(false); onSaved(); }} />}
      </Td>
    </tr>
  );
};

// q: the page's search box. While it has text, every matching old account is
// shown (not just the ones needing attention), so a search never hides a hit.
export const OldPlatformAccounts = ({ canEdit, q = "" }) => {
  const { api } = useApp();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [all, setAll] = useState(false);
  const [showIgnored, setShowIgnored] = useState(false);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    setErr(null);
    api.get("getBankMigrationAccounts").then(setData).catch(e => setErr(e.message));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRunning(true); setMsg(null);
    try {
      const r = await api.post("runBankMigrationRefresh", {});
      setMsg(`${num(r.newrows)} new transactions brought across, ${num(r.updatedrows)} corrected.`);
      load();
    } catch (e) { setMsg("Failed: " + e.message); }
    setRunning(false);
  };

  if (err) return <Card style={{ padding: 14, marginTop: 18, fontSize: 12, color: "#993C1D" }}>{err}</Card>;
  if (!data) return null;

  const rows = data.rows || [];
  const attention = rows.filter(r => r.status !== "Up to date" && r.status !== "Ignored");
  const ignoredRows = rows.filter(r => r.status === "Ignored");
  const term = String(q || "").trim().toLowerCase();
  const hit = r => [r.BankAccountId, r.sourcename, r.sourcemask, r.sourcebank, r.companyname, r.ace_bankname,
                    r.ace_last4, r.sample, r.status].some(v => String(v ?? "").toLowerCase().includes(term));
  const shown = term ? rows.filter(hit)
    : showIgnored ? ignoredRows
    : (all ? rows.filter(r => r.status !== "Ignored") : attention);
  const waiting = rows.filter(r => r.status === "Transactions waiting").reduce((s, r) => s + Math.max(0, (r.src_posted || 0) - (r.landed_txns || 0)), 0);

  return (
    <Card style={{ marginTop: 18, overflowX: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Old platform (ACE 1.0) accounts</div>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          {attention.length ? `${attention.length} need attention` : "All mapped and up to date"}
          {waiting ? ` · about ${num(waiting)} transactions waiting` : ""}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Btn onClick={() => { setAll(v => !v); setShowIgnored(false); }}>{all && !showIgnored ? "Needs attention only" : `Show all (${rows.length - ignoredRows.length})`}</Btn>
          {ignoredRows.length > 0 && <Btn onClick={() => setShowIgnored(v => !v)}>{showIgnored ? "Hide ignored" : `Show ignored (${ignoredRows.length})`}</Btn>}
          {canEdit && <Btn variant="primary" disabled={running} onClick={refresh}>{running ? "Bringing across…" : "Bring transactions across"}</Btn>}
        </div>
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 10 }}>
        Old Plaid connections still feed ACE 1.0; its transactions are copied into <code>TransactionMIGRATION</code>.
        Give each old account its borrower and its bank account here, then <b>Bring transactions across</b>. Accounts appear here
        even before any of their transactions have landed — a borrower that moved banks shows up as <i>Needs bank account</i>.
        Re-running is safe: nothing the live Plaid sync owns is touched.
      </div>
      {msg && <div style={{ fontSize: 12, color: B.cyanDark, marginBottom: 8 }}>{msg}</div>}
      {shown.length === 0
        ? <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: "var(--color-text-secondary)" }}>
            {term ? "No old accounts match that search." : showIgnored ? "Nothing is ignored." : "Nothing needs attention."}</div>
        : <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <Th>Old account</Th><Th>In ACE 1.0</Th><Th>In ACE</Th><Th>Borrower</Th><Th>Bank account</Th><Th>Status</Th><Th></Th>
            </tr></thead>
            <tbody>{shown.map(r => (
              <Row key={r.BankAccountId + ":" + (r.accountid || "") + ":" + (r.acebankaccountid || "")} r={r}
                   accounts={data.accounts || []} bankAccounts={data.bankAccounts || []} onSaved={load} canEdit={canEdit} />
            ))}</tbody>
          </table>}
    </Card>
  );
};
